import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { isHttpClientError } from '@brightlocal/api-client';

// Imported from the module path (not the `shared/utils` barrel) to avoid a
// require cycle: the barrel transitively pulls in `shared/services`, which
// imports this file.
import { reportRequestError } from 'shared/utils/reportRequestError';

import { ENVIRONMENTS, env } from '../config/env';

// The Feature Mocker runs only in the `local` and `dev` environments (see main.tsx), where it
// bypasses unmocked endpoints to the real backend. When no backend is running those calls fail,
// and a retry adds a ~1s backoff wait per query before the page settles — the "slow navigation".
// Skip retries in those environments so failures resolve immediately; keep one retry elsewhere
// for transient network errors. Gate on VITE_ENV, not `import.meta.env.DEV` — the latter is false
// for the `dev` PR preview builds (production builds), where the mocker is still active.
// Read env lazily (inside `retry`) so merely importing this singleton never touches
// `config/env` at module-eval time — importers stay side-effect-free and test-safe.
const isMockedEnvironment = () =>
  env.environment === ENVIRONMENTS.LOCAL || env.environment === ENVIRONMENTS.DEVELOPMENT;

const MAX_RETRIES = 1;
const NO_RETRIES = 0;

/**
 * Default retry policy for every query. A 4xx is a final answer (no access, not found, bad
 * request) and is requested once; network and 5xx failures get one retry outside the mocked
 * environments.
 */
export const shouldRetryQuery = (failureCount: number, error: unknown) => {
  const maxRetries = isMockedEnvironment() ? NO_RETRIES : MAX_RETRIES;

  return !isHttpClientError(error) && failureCount < maxRetries;
};

// Initialize and export a singleton instance of QueryClient for use throughout the app.
// This allows consistent query caching, fetching, and state management with TanStack Query.
export const queryClient = new QueryClient({
  // Central Sentry reporting for every query and mutation failure. Fires once per
  // settled error (after retries), reading per-request context from
  // `meta.errorReporting`. Replaces the per-`queryFn` try/catch TanStack v5 forced
  // after removing the per-query `onError` callback.
  queryCache: new QueryCache({
    onError: (error, query) => reportRequestError(error, query.meta)
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => reportRequestError(error, mutation.meta)
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery
    }
  }
});
